import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { useServiceItemCatalog } from '@/hooks/useSettings'
import { DEFAULT_CATALOG_IMAGE_URL } from '@/lib/catalogImages'
import { formatCurrency } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { ServiceItem } from '@/types'

// The minimal slice a line editor needs back when an item is chosen. The full
// ServiceItem is also handed over (second arg of onSelect) for callers that
// want everything.
export interface ServiceItemSelection {
  id: string
  name: string
  description: string | null
  unit_price: number
  unit: string | null
  tax_rate: number | null
  image_file_id: string | null
  image: string | null
}

export interface ServiceItemPickerProps {
  /** Currently-selected service item id (controlled). */
  value?: string | null
  /** Fired with the normalised selection and the full ServiceItem record. */
  onSelect: (selection: ServiceItemSelection, item: ServiceItem) => void
  /** Disable the trigger (e.g. while a parent row is read-only). */
  disabled?: boolean
  /** Override the placeholder shown when nothing is picked. */
  placeholder?: string
  className?: string
  /** Optional thumbnail styling for contexts with a dedicated avatar treatment. */
  imageClassName?: string
  /** Decimal places for unit price display (default 0). */
  fractionDigits?: number
  /** Secondary line shown for the selected item. */
  selectedSecondary?: 'price' | 'description'
  /** Fallback label while the selected item is not in the current options page. */
  selectedName?: string | null
  /** Fallback description while the selected item is not in the current options page. */
  selectedDescription?: string | null
  /** Compact single-line trigger (Figma contract form). */
  compact?: boolean
}

function toSelection(item: ServiceItem): ServiceItemSelection {
  return {
    id: item.id,
    name: item.name,
    description: item.description,
    unit_price: item.unit_price,
    unit: item.unit,
    tax_rate: item.tax_rate ?? null,
    image_file_id: item.image_file_id ?? null,
    image: item.image_url ?? null,
  }
}

export function ServiceItemImage({
  src,
  className,
  loading,
}: {
  src?: string | null
  className?: string
  loading?: 'eager' | 'lazy'
}) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const isDefaultImage = !src || failedSrc === src

  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded bg-[#ffece6]',
        className
      )}
    >
      <img
        src={isDefaultImage ? DEFAULT_CATALOG_IMAGE_URL : src}
        alt=''
        className={
          isDefaultImage
            ? 'h-1/2 w-1/2 object-contain'
            : 'size-full object-cover'
        }
        loading={loading}
        decoding={loading === 'lazy' ? 'async' : undefined}
        onError={() => {
          if (!isDefaultImage && src) setFailedSrc(src)
        }}
      />
    </span>
  )
}

// Catalogue combobox over Settings > Service items. Loads the catalogue via the
// existing useServiceItems hook, shows each item's thumbnail when present, and
// returns the chosen item to the caller. Used later by quote/invoice/contract
// line editors (#27, #40, #57).
export function ServiceItemPicker({
  value,
  onSelect,
  disabled,
  placeholder,
  className,
  imageClassName,
  fractionDigits = 0,
  selectedSecondary = 'price',
  selectedName,
  selectedDescription,
  compact = false,
}: ServiceItemPickerProps) {
  const { t } = useTranslation('shared')
  // Pull a generous page so the combobox lists the active catalogue. Feature
  // phases can swap this for a server-side search if the catalogue grows.
  const { data, isLoading, isError } = useServiceItemCatalog()

  const items = useMemo(
    () => (data?.data ?? []).filter(it => it.is_active),
    [data]
  )

  const handleChange = (id: string) => {
    const item = items.find(it => it.id === id)
    if (item) onSelect(toSelection(item), item)
  }

  const selected = value ? items.find(it => it.id === value) : undefined
  const displayName = selected?.name ?? selectedName ?? ''
  const displayDescription =
    selected?.description ?? selectedDescription ?? null
  const resolvedPlaceholder = isError
    ? t('serviceItemPicker.error')
    : isLoading
      ? t('serviceItemPicker.loading')
      : (placeholder ?? t('serviceItemPicker.placeholder'))

  const fmtPrice = (value: number) =>
    formatCurrency(value, 'EUR', fractionDigits)
  const secondaryLabel =
    selectedSecondary === 'description'
      ? (displayDescription?.trim() ?? '')
      : selected
        ? `${fmtPrice(selected.unit_price)}${selected.unit ? ` / ${selected.unit}` : ''}`
        : ''

  return (
    <Select
      value={value ?? undefined}
      onValueChange={handleChange}
      disabled={disabled || isLoading || isError}
    >
      <SelectTrigger
        className={cn(
          'h-auto min-h-10 w-full items-center py-2 text-left [&>span]:min-w-0 [&>span]:line-clamp-none [&>span]:whitespace-normal',
          className
        )}
      >
        <SelectValue placeholder={resolvedPlaceholder}>
          {displayName ? (
            compact ? (
              <span className='min-w-0 truncate'>{displayName}</span>
            ) : (
              <span className='flex w-full min-w-0 items-center gap-2'>
                <ServiceItemImage
                  src={selected?.image_url ?? null}
                  className={cn('h-12 w-12', imageClassName)}
                />
                <span className='flex min-w-0 flex-1 flex-col items-start gap-2 leading-tight'>
                  <span className='w-full truncate text-sm font-medium text-foreground'>
                    {displayName}
                  </span>
                  {secondaryLabel ? (
                    selectedSecondary === 'description' ? (
                      <TruncatedTextTooltip
                        className='w-full whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground'
                        content={secondaryLabel}
                      >
                        {secondaryLabel}
                      </TruncatedTextTooltip>
                    ) : (
                      <span className='w-full truncate text-xs text-muted-foreground'>
                        {secondaryLabel}
                      </span>
                    )
                  ) : null}
                </span>
              </span>
            )
          ) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {items.length === 0 && !isLoading && (
          <div className='px-2 py-3 text-center text-sm text-muted-foreground'>
            {t('serviceItemPicker.empty')}
          </div>
        )}
        {items.map(item => (
          <SelectItem key={item.id} value={item.id} textValue={item.name}>
            <span className='flex items-center gap-2'>
              <ServiceItemImage
                src={item.image_url}
                className={cn('h-12 w-12', imageClassName)}
              />
              <span className='flex flex-col items-start leading-tight'>
                <span className='font-medium'>{item.name}</span>
                <span className='text-xs text-muted-foreground'>
                  {fmtPrice(item.unit_price)}
                  {item.unit ? ` / ${item.unit}` : ''}
                </span>
              </span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export default ServiceItemPicker
